<template>
  <div class="geometric-transform-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.3;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-gt"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.04)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- Axes -->
           <line x1="400" y1="0" x2="400" y2="600" stroke="#334155" stroke-width="2" />
           <line x1="0" y1="300" x2="800" y2="300" stroke="#334155" stroke-width="2" />
           <text x="410" y="325" fill="#64748b" font-size="18" font-weight="900">O (0,0)</text>

           <!-- Origin reference -->
           <circle cx="400" cy="300" r="4" fill="#64748b" />

           <!-- 原图形 (虚线基底表示) -->
           <g transform="translate(400, 300)">
              <polygon :points="basePolyPoints" fill="rgba(56, 189, 248, 0.15)" stroke="#38bdf8" stroke-width="3" stroke-dasharray="8,6" />
              <circle v-for="(pt, idx) in baseShape" :key="'base'+idx" :cx="pt.x" :cy="pt.y" r="5" fill="#38bdf8" />
              <text v-for="(pt, idx) in baseShape" :key="'txtl'+idx" :x="pt.x+10" :y="pt.y-10" fill="#7dd3fc" font-size="16" font-weight="900" style="text-shadow: 0 0 5px #000">P{{ idx+1 }}</text>
           </g>

           <!-- 变换后的图象 (实线高亮) -->
           <g transform="translate(400, 300)">
              <polygon :points="transformedPolyPoints" fill="rgba(244, 63, 94, 0.3)" stroke="#f43f5e" stroke-width="5" filter="url(#glow-gt)"/>
              <circle v-for="(pt, idx) in transformedShape" :key="'t'+idx" :cx="pt.x" :cy="pt.y" r="6" fill="#f43f5e" stroke="#fff" stroke-width="2" />
              <text v-for="(pt, idx) in transformedShape" :key="'txtt'+idx" :x="pt.x+10" :y="pt.y+20" fill="#fca5a5" font-size="16" font-weight="900" style="text-shadow: 0 0 5px #000">P{{ idx+1 }}'</text>
              
              <!-- 轨迹追踪线：展示顶点是如何跑到新位置的 -->
              <line v-if="mode === 'translation'" v-for="(pt, idx) in baseShape" :key="'tr'+idx" 
                 :x1="pt.x" :y1="pt.y" :x2="transformedShape[idx].x" :y2="transformedShape[idx].y" 
                 stroke="#facc15" stroke-dasharray="6,4" stroke-width="2" />
                 
              <!-- 反射变换的对称轴 (Mirror Line) -->
              <g v-if="mode === 'reflection'">
                 <line :x1="-400" :y1="getMirrorLineY(-400)" :x2="400" :y2="getMirrorLineY(400)" stroke="#10b981" stroke-width="4" stroke-dasharray="12,6" />
                 <text :x="-380" :y="getMirrorLineY(-380) - 15" fill="#34d399" font-size="20" font-weight="900" style="text-shadow: 0 0 5px #000">对称轴 L = {{ mirrorAxisAngle }}°</text>
                 
                 <!-- 垂直辅助线证明反射特质 -->
                 <line v-for="(pt, idx) in baseShape" :key="'rf'+idx" 
                    :x1="pt.x" :y1="pt.y" :x2="transformedShape[idx].x" :y2="transformedShape[idx].y" 
                    stroke="#10b981" stroke-dasharray="4,4" stroke-width="2" opacity="0.6" />
              </g>

              <!-- 旋转圆弧轨迹 -->
              <g v-if="mode === 'rotation'">
                 <!-- 以原点为圆心 -->
                 <path v-for="(pt, idx) in baseShape" :key="'arc'+idx" :d="calcArc(pt, rotAngle)" fill="none" stroke="#fbbf24" stroke-width="2" stroke-dasharray="4,6" />
                 <!-- 连向中心的线 -->
                 <line v-for="(pt, idx) in baseShape" :key="'rc1'+idx" x1="0" y1="0" :x2="pt.x" :y2="pt.y" stroke="#fbbf24" stroke-width="1.5" opacity="0.3"/>
                 <line v-for="(pt, idx) in transformedShape" :key="'rc2'+idx" x1="0" y1="0" :x2="pt.x" :y2="pt.y" stroke="#fbbf24" stroke-width="1.5" opacity="0.8"/>
              </g>
           </g>

        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">拓扑与几何代数模型</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            <div class="eq-box text-center">
               <div class="eq-display text-blue">当前映射矩阵解析</div>
               <div class="seq-data-list">
                  <template v-if="mode === 'translation'">
                     <div class="data-row"><span>水平平移 (Δx)</span><span class="hl">{{ transX }}</span></div>
                     <div class="data-row"><span>垂直平移 (Δy)</span><span class="hl">{{ transY }}</span></div>
                     <div class="data-row" style="background: rgba(56,189,248,0.1)"><span>平移算子 T(x,y)</span><span class="hl text-blue">(x+{{ transX }}, y+{{ transY }})</span></div>
                  </template>
                  
                  <template v-if="mode === 'rotation'">
                     <div class="data-row"><span>旋转中心</span><span class="hl">原点 O(0,0)</span></div>
                     <div class="data-row"><span>旋转角度 θ</span><span class="hl">{{ rotAngle }}°</span></div>
                     <div class="data-row" style="background: rgba(250,204,21,0.1)"><span>旋转矩阵 R(θ)</span>
                        <div class="hl text-yellow" style="text-align: right; font-size: 12px;">[{{ Math.cos(rotAngle*Math.PI/180).toFixed(2) }} , {{ -Math.sin(rotAngle*Math.PI/180).toFixed(2) }}]<br/>[{{ Math.sin(rotAngle*Math.PI/180).toFixed(2) }} , {{ Math.cos(rotAngle*Math.PI/180).toFixed(2) }}]</div>
                     </div>
                  </template>

                  <template v-if="mode === 'reflection'">
                     <div class="data-row"><span>反射轴倾角</span><span class="hl">{{ mirrorAxisAngle }}°</span></div>
                     <div class="data-row"><span>几何守恒量</span><span class="hl text-green">线段长度, 面积不变量</span></div>
                     <div class="data-row" style="background: rgba(16,185,129,0.1)"><span>对应点连线</span><span class="hl text-green">必被对称轴垂直平分！</span></div>
                  </template>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2; display: flex; flex-direction: column; width: 100%;">
               <label>映射算法 (刚体变换几何算子)</label>
               <el-radio-group v-model="mode" size="small" style="display: flex; flex-wrap: wrap;">
                 <el-radio-button label="translation">平移变换 (Translation)</el-radio-button>
                 <el-radio-button label="rotation">旋转变换 (Rotation)</el-radio-button>
                 <el-radio-button label="reflection">反射变换 (Reflection Mirroring)</el-radio-button>
               </el-radio-group>
            </div>
            
            <template v-if="mode === 'translation'">
               <div class="s-item">
                  <label>水平平移 X 轴</label>
                  <el-slider v-model="transX" :min="-300" :max="300" :step="10" size="small"></el-slider>
               </div>
               <div class="s-item">
                  <label>垂直平移 Y 轴 (视觉上正下为Y+)</label>
                  <el-slider v-model="transY" :min="-200" :max="200" :step="10" size="small"></el-slider>
               </div>
            </template>

            <template v-if="mode === 'rotation'">
               <div class="s-item" style="grid-column: span 2;">
                  <label>整体绕原点旋转角度 θ</label>
                  <el-slider v-model="rotAngle" :min="0" :max="360" :step="5" size="small"></el-slider>
               </div>
            </template>

            <template v-if="mode === 'reflection'">
               <div class="s-item" style="grid-column: span 2;">
                  <label>旋转对称轴 L 的角度 (0°为水平X轴)</label>
                  <el-slider v-model="mirrorAxisAngle" :min="0" :max="180" :step="5" size="small"></el-slider>
               </div>
            </template>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GeometricTransformViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'reflection',
      transX: 150,
      transY: -50,
      rotAngle: 60,
      mirrorAxisAngle: 45,
      // The base shape vertices (a simple asymmetric polygon)
      baseShape: [
         {x: -150, y: -50}, 
         {x: -50, y: -50}, 
         {x: -100, y: -150}
      ]
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       if (this.mode === 'translation') return '平移：将坐标向量均匀叠加的过程，不改变物体本身方向。';
       if (this.mode === 'rotation') return '旋转：绕定点的等距圆周运动，矩阵乘法的直观映射。';
       if (this.mode === 'reflection') return '反射：穿透对称轴的镜像翻转，它颠倒了左右手性。';
       return '图形变化与几何代数变换';
    },
    activeFormula() {
       if (this.mode === 'translation') return 'T(x) = x + v';
       if (this.mode === 'rotation') return 'x\' = R(θ) · x';
       if (this.mode === 'reflection') return 'S_l(x) = x - 2(x·n)n / |n|²';
       return 'Affine Transform';
    },
    basePolyPoints() {
       return this.baseShape.map(p => `${p.x},${p.y}`).join(' ');
    },
    transformedShape() {
       if (this.mode === 'translation') {
          return this.baseShape.map(p => ({
             x: p.x + this.transX,
             y: p.y + this.transY
          }));
       }
       if (this.mode === 'rotation') {
          const rad = this.rotAngle * Math.PI / 180;
          const cosA = Math.cos(rad);
          const sinA = Math.sin(rad);
          return this.baseShape.map(p => ({
             x: p.x * cosA - p.y * sinA,
             y: p.x * sinA + p.y * cosA
          }));
       }
       if (this.mode === 'reflection') {
          // Reflection across line passing through origin with angle alpha
          const rad = this.mirrorAxisAngle * Math.PI / 180;
          // Normal vector to the mirror line: (-sin, cos)
          const nx = -Math.sin(rad);
          const ny = Math.cos(rad);
          
          return this.baseShape.map(p => {
             const dotProduct = p.x * nx + p.y * ny; // Since |n|=1
             return {
                x: p.x - 2 * dotProduct * nx,
                y: p.y - 2 * dotProduct * ny
             };
          });
       }
       return this.baseShape;
    },
    transformedPolyPoints() {
       return this.transformedShape.map(p => `${p.x},${p.y}`).join(' ');
    },
    tipDesc() {
       if (this.mode === 'translation') return '平移就像一列火车整体移动。没有任何点的相对位置发生变化。在代数上，它展现为各个坐标轴加上常数 (x+a, y+b)。这证明了向量加法的几何平移不变性。';
       if (this.mode === 'rotation') return '在旋转中，所有的点仿佛被一根无形的绳索拴在原点上。旋转不改变图形的形状与大小(等距映射)，但改变了朝向。这就是正交矩阵(R)的作用，其行列式始终等于 1。';
       if (this.mode === 'reflection') return '这是极其奇妙的对称魔法！反射能立刻使所有的东西变成“镜像(反转的手性)”，因为它的行列式等于 -1。无论原来的线段有多长、夹角多大，反射后全部相等，唯一的代价是：你无法通过平移加旋转来将原图形“放置”回反射图内，除非离开二维平面！';
       return '';
    }
  },
  methods: {
    detectMode() {
      const kp = (this.knowledgePoint || '').toString();
      if (/反射|轴对称/.test(kp)) {
         this.mode = 'reflection';
      } else if (/平移/.test(kp)) {
         this.mode = 'translation';
      } else if (/坐标系/.test(kp)) {
         this.mode = 'axis';
      } else {
         this.mode = 'rotation';
      }
    },
    briefText() {
       if (this.mode === 'axis') return '平面直角坐标系：点在二维空间中的精确定位';
       if (this.mode === 'reflection') return '对称变换：镜像反射与轴对称性质';
       if (this.mode === 'translation') return '平移变换：沿矢量矢量的整体位移';
       return '旋转变换：绕定点的角位移演示';
    },
    theoryText() {
       if (this.mode === 'axis') return '坐标系是解析几何的基石。通过横轴(x)和纵轴(y)，每一个抽象的点都被赋予了唯一的数值对。这实现了“形”与“数”的完美统一。';
       if (this.mode === 'reflection') return '轴对称变换保持图形的形状和大小不变，但改变其手性（定向）。对应点到对称轴的距离相等。';
       return '刚体变换（旋转、平移）保持了图形的所有几何性质（长度、角度、面积）不变。';
    },
    getMirrorLineY(x) {
       const rad = this.mirrorAxisAngle * Math.PI / 180;
       // y = x * tan(rad)
       // Due to SVG coordinate Y being flipped downwards, we might have to flip?
       // Let's just use raw tan and see, as our reflection logic is consistent.
       return Math.tan(rad) * x;
    },
    calcArc(pt, angleDeg) {
       // Draws arc from pt(x,y) to rotated pt. Counter-clockwise or clockwise depending on Y axis flip.
       // The math here simply maps an SVG path elliptical arc between original and new point.
       const r = Math.sqrt(pt.x*pt.x + pt.y*pt.y);
       if (r === 0) return '';
       
       const rad = angleDeg * Math.PI / 180;
       const nx = pt.x * Math.cos(rad) - pt.y * Math.sin(rad);
       const ny = pt.x * Math.sin(rad) + pt.y * Math.cos(rad);
       
       // sweep-flag: usually 1, but depends on angle polarity and axis
       const largeArcFlag = angleDeg > 180 ? 1 : 0;
       return `M ${pt.x} ${pt.y} A ${r} ${r} 0 ${largeArcFlag} 1 ${nx} ${ny}`;
    }
  }
}
</script>

<style scoped lang="scss">
.geometric-transform-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-yellow { color: #facc15; } .text-green { color: #34d399; } .text-blue { color: #38bdf8; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 18px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
